import React from 'react';
import { useInView } from 'framer-motion';

/* Turns any doodle into a pen: every stroke is normalised to a path length of
   1 so one keyframe draws all of them, and each stroke starts a beat after the
   last. The attribute is written in an effect rather than authored into every
   path so the doodles stay readable as drawings. */
function useDrawIn(draw: boolean) {
    const ref = React.useRef<SVGSVGElement>(null);
    const inView = useInView(ref, { once: true, margin: '-10% 0px -10% 0px' });

    React.useEffect(() => {
        if (!draw) return;
        const el = ref.current;
        if (!el) return;
        el.querySelectorAll<SVGGeometryElement>('path, circle').forEach((node, i) => {
            node.setAttribute('pathLength', '1');
            node.style.setProperty('--i', String(i));
        });
    }, [draw]);

    return {
        ref,
        cls: draw ? ['hand-doodle--draw', inView ? 'is-drawn' : ''].filter(Boolean).join(' ') : '',
    };
}

/* One frame for every margin doodle: same box handling, same pen. */
function Doodle({
    viewBox,
    className = '',
    style,
    draw = false,
    children,
}: {
    viewBox: string;
    className?: string;
    style?: React.CSSProperties;
    draw?: boolean;
    children: React.ReactNode;
}) {
    const pen = useDrawIn(draw);
    return (
        <svg
            ref={pen.ref}
            style={style}
            className={['hand-doodle', pen.cls, className].filter(Boolean).join(' ')}
            viewBox={viewBox}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {children}
        </svg>
    );
}

/* A closed ring drawn off true. Two low harmonics deform the radius, the same
   way the loop ring is deformed, so a circle or a coin's rim reads as drawn by
   hand rather than struck with a compass. */
function wobbleRing(
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    { amp = 0.03, phase = 0.6, samples = 56 } = {},
) {
    const pts: string[] = [];
    for (let i = 0; i < samples; i += 1) {
        const t = (-90 + (360 * i) / samples) * (Math.PI / 180);
        const k = 1 + amp * Math.sin(3 * t + phase) + amp * 0.55 * Math.cos(5 * t + phase * 2);
        pts.push(`${(cx + rx * k * Math.cos(t)).toFixed(1)} ${(cy + ry * k * Math.sin(t)).toFixed(1)}`);
    }
    return `M${pts[0]}L${pts.slice(1).join('L')}Z`;
}

type Direction = 'right' | 'down-right' | 'down' | 'left';

const ROTATION: Record<Direction, number> = {
    right: 0,
    'down-right': 42,
    down: 90,
    left: 180,
};

/* A single-stroke arrow: a shaft that drifts off true and an open, uneven head.
   Authored pointing right; the other directions are the same hand, rotated. */
export function HandArrow({
    direction = 'right',
    draw = false,
    delay = 0,
    width = 60,
    className = '',
}: {
    direction?: Direction;
    draw?: boolean;
    /* Milliseconds to hold the ink back, for hero choreography. */
    delay?: number;
    width?: number;
    className?: string;
}) {
    const ref = React.useRef<SVGSVGElement>(null);
    const inView = useInView(ref, { once: true, margin: '-10%' });

    return (
        <svg
            ref={ref}
            className={['hand-arrow', draw ? 'hand-arrow--draw' : '', draw && inView ? 'is-drawn' : '', className]
                .filter(Boolean)
                .join(' ')}
            style={{ width, rotate: `${ROTATION[direction]}deg`, '--draw-delay': `${delay}ms` } as React.CSSProperties}
            viewBox="0 0 64 32"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M3 21.4C13.6 12.2 24.4 22.6 36.2 17.4 43.6 14.2 49.2 12.5 56 12.1" />
            <path d="M56 12.1 44.6 4.8" />
            <path d="M56 12.1 49.2 22.6" />
        </svg>
    );
}

/* A robot arm reaching for a block. Hero margin. */
export function DoodleArm({ className = '', draw = false }: { className?: string; draw?: boolean }) {
    return (
        <Doodle viewBox="0 0 146 118" className={className} draw={draw}>
            <path d="M8 107.6C42 106.4 104 106.6 138 107.8" />
            <path d="M21.6 107.2 25 93.4 41 93 44.6 107.4" />
            <path d="M33 92.6C41.4 79 50.6 65.4 58.4 53" />
            <path d="M60.6 50.6C74.4 43.8 90.6 43.6 103.6 49.6" />
            <path d="M103.8 49.4 104.4 59.6" />
            <circle cx="32.8" cy="92.8" r="3.4" />
            <circle cx="59.4" cy="51.4" r="3.6" />
            <path d="M94.2 58.6C101.4 59.8 108.4 61.4 115 63.6" />
            <path d="M96 59.2C97.4 65.8 97.8 72.2 96.4 78.4" />
            <path d="M112.8 62.4C114 68.6 114 74.6 112.8 80.6" />
            <path d="M95.4 93.2 115.4 91 117.6 106 96.8 107.2Z" />
        </Doodle>
    );
}

/* A person pointing at a robot arm that holds a block. Teaching margin. */
export function DoodleTeach({ className = '', draw = false }: { className?: string; draw?: boolean }) {
    return (
        <Doodle viewBox="0 0 170 120" className={className} draw={draw}>
            <path d="M10 107.4C54 106.2 116 106.4 162 107.6" />
            <circle cx="26" cy="26" r="11" />
            <path d="M26.2 37.2C25.8 49 26.2 62.6 26 74.2" />
            <path d="M26 74.2 16.6 104.6" />
            <path d="M26 74.2 36.4 104.2" />
            <path d="M26.4 48.6 15.2 66.4" />
            <path d="M26.6 47.6C33.4 46.6 40 45.4 45.8 44.2" />
            <path d="M45.8 44.2C51.6 42.6 57.4 41 62.4 39.8" />
            <path d="M62.4 39.8 68.6 38.4" />
            <path d="M80.4 36.4 98.4 35.8 98 54.4 79.8 54Z" />
            <path d="M116 107 120.4 94.2 142 94 146.4 106.8" />
            <path d="M131 94.2C131.4 86 131.2 77.6 131 70.4" />
            <circle cx="131" cy="70.2" r="3.6" />
            <path d="M130.6 70C126.4 62 122.2 53.8 118.4 46.4" />
            <circle cx="118.2" cy="46.2" r="3.4" />
            <path d="M117.6 46.4 108 44.8" />
            <path d="M107.8 38.2C107.4 42.6 107.4 46.6 107.6 50.6" />
            <path d="M107.8 38.4 101 36.8" />
            <path d="M107.6 50.6 100.8 52.4" />
        </Doodle>
    );
}

/* A wheeled robot with a camera and an antenna. Use-case margin. */
export function DoodleRover({ className = '', draw = false }: { className?: string; draw?: boolean }) {
    return (
        <Doodle viewBox="0 0 150 118" className={className} draw={draw}>
            <path d="M28.4 52.4 118 51.6 117.4 88.4 28 87.8Z" />
            <circle cx="49" cy="95" r="13" />
            <circle cx="101" cy="95" r="13" />
            <circle cx="49" cy="95" r="3.2" />
            <circle cx="101" cy="95" r="3.2" />
            <path d="M62.4 52 62.6 36.4 86.4 36 86.2 52" />
            <circle cx="74.4" cy="44" r="5" />
            <path d="M40.4 51.8C40.6 43 40.4 35.4 40.2 28.6" />
            <circle cx="40.2" cy="25" r="3.4" />
        </Doodle>
    );
}

/* A chip with a bolt through it: the policy, fitted to the device. Proof band. */
export function DoodleChip({ className = '', draw = false }: { className?: string; draw?: boolean }) {
    return (
        <Doodle viewBox="0 0 130 118" className={className} draw={draw}>
            <path d="M34.4 34.4 96 33.8 95.6 96.4 34 95.8Z" />
            <path d="M48 34.2 47.8 22" />
            <path d="M65.2 34.2 65 22" />
            <path d="M82.2 34.2 82 22" />
            <path d="M48 96.2 47.8 108.4" />
            <path d="M65.2 96.2 65 108.4" />
            <path d="M82.2 96.2 82 108.4" />
            <path d="M34.2 48 22 47.8" />
            <path d="M34.2 65.2 22 65" />
            <path d="M34.2 82.2 22 82" />
            <path d="M96 48 108.4 47.8" />
            <path d="M96 65.2 108.4 65" />
            <path d="M96 82.2 108.4 82" />
            <path d="M68.4 42.4 52 70.2 64.4 69.8 60.2 90 80.4 60 68 60.4Z" />
        </Doodle>
    );
}

/* A magnifier for the diagnose stage of the ring. */
export function DoodleGlass({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
    return (
        <Doodle viewBox="0 0 60 60" className={className} style={style}>
            <circle cx="24.6" cy="24" r="15" />
            <path d="M35.4 34.6 51.6 51.4" />
            <path d="M16.6 20.4C18.4 15.6 22.4 12.4 27.6 11.8" />
        </Doodle>
    );
}

/* A sim window with a block in it, for the simulation stage of the ring. */
export function DoodleSim({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
    return (
        <Doodle viewBox="0 0 60 60" className={className} style={style}>
            <path d="M6.4 12.4 54 11.8 53.6 50.2 6 49.6Z" />
            <path d="M6.2 21 53.8 20.4" />
            <circle cx="12.6" cy="16.4" r="1.9" />
            <circle cx="19.4" cy="16.4" r="1.9" />
            <path d="M22 32.4 38 32 38.2 46.2 22.2 46Z" />
            <path d="M22 32.4 28.2 26.2 44.2 26 38 32.2" />
            <path d="M38.2 32.2 44.2 26 44 40.2 38.2 46.2" />
        </Doodle>
    );
}

/* A stack of written-on pages with a pen across them. Blog masthead. */
export function DoodlePapers({ className = '', draw = false }: { className?: string; draw?: boolean }) {
    return (
        <Doodle viewBox="0 0 140 130" className={className} draw={draw}>
            <path d="M26.4 30.4 106 29.8 105.6 92.4 26 91.8Z" />
            <path d="M40 46.6 92.4 46" />
            <path d="M40 58.6 92.2 58" />
            <path d="M40 70.6 78.4 70.2" />
            <path d="M105.8 38.4 114 38 113.6 100.4 34 99.8 34.2 92" />
            <path d="M56.4 108.2 100.4 76.4 107.4 86.2 63.4 118" />
            <path d="M56.4 108.2 46 122.4 63.4 118" />
            <path d="M92.2 82.4 99.2 92.4" />
        </Doodle>
    );
}

/* A stopwatch, one swept arc outside the dial. Cycle-time tile. */
export function DoodleStopwatch({ className = '', draw = false }: { className?: string; draw?: boolean }) {
    return (
        <Doodle viewBox="20 20 92 88" className={className} draw={draw}>
            <path d={wobbleRing(58, 70, 34, 34, { amp: 0.028, phase: 0.9 })} />
            <path d="M49.6 24.6 66.8 24.2" />
            <path d="M58.2 24.4 58 36" />
            <path d="M58 70 58.4 49.4" />
            <path d="M58 70 73.6 78.4" />
            <path d="M98.4 43.6C106.4 52.4 108.6 66.2 104.4 77.6" />
            <path d="M104.4 77.6 98.2 71.4" />
            <path d="M104.4 77.6 108.4 69.6" />
        </Doodle>
    );
}

/* Three robots on one dashed line: one fix reaches every system. Each box is
   four separate strokes that bow a little and miss their corners, the antennas
   lean at different angles, and the links are dashes of uneven length. */
export function DoodleFleet({ className = '', draw = false }: { className?: string; draw?: boolean }) {
    return (
        <Doodle viewBox="2 20 160 70" className={className} draw={draw}>
            <path d="M7.2 45.4Q26 43.6 45.6 44.9" />
            <path d="M46.4 45.8Q47.6 64.2 45.2 83.2" />
            <path d="M45 84.6Q26.4 86.2 6.8 84.1" />
            <path d="M6.2 83.2Q4.8 64.4 6.6 44.4" />
            <path d="M26.8 43.9Q26.1 36.6 25.3 30.9" />
            <circle cx="25.1" cy="27.3" r="3.4" />

            <path d="M49.4 64.7Q51 64.1 52.6 64.5" />
            <path d="M55.2 64.2Q58 64.8 60.6 64.1" />

            <path d="M63.6 44.5Q82 46.2 101.2 44.7" />
            <path d="M102.4 45.6Q101 64.2 102.2 83.5" />
            <path d="M101.2 84.3Q82 83 62.4 84.7" />
            <path d="M62.1 83.6Q63.7 64 62.4 45.4" />
            <path d="M82.6 44.2Q83.5 37 84.1 30.7" />
            <circle cx="84.4" cy="27.2" r="3.4" />

            <path d="M105.6 64.4Q108.8 65.1 111.6 64.3" />
            <path d="M114 63.9Q115.4 64.4 116.6 64.2" />

            <path d="M119.6 45.1Q138 43.7 157.2 45.2" />
            <path d="M158.3 46Q159.6 64.8 157.5 83.7" />
            <path d="M157 84.4Q138 86 118.6 84.1" />
            <path d="M118.2 83.1Q117 64 118.7 44.7" />
            <path d="M138.3 44.4Q137 37.4 136.1 30.9" />
            <circle cx="135.8" cy="27.4" r="3.4" />
        </Doodle>
    );
}

/* A stack of coins and an arrow going the other way. Cost tile. */
export function DoodleCoins({ className = '', draw = false }: { className?: string; draw?: boolean }) {
    return (
        <Doodle viewBox="16 26 80 60" className={className} draw={draw}>
            <path d={wobbleRing(40, 50, 20, 6.6, { amp: 0.04, phase: 0.4, samples: 44 })} />
            <path d="M20.2 50.2C20 60.6 20.4 71.6 20 82" />
            <path d="M60 49.8C60.2 60.2 59.8 71.2 60.2 81.6" />
            <path d="M20.4 58.2C23.6 62 30.4 64.2 40.2 64 49 63.8 56.4 61.6 59.8 57.8" />
            <path d="M20.2 66.4C23.4 70.2 30.2 72.4 40 72.2 48.8 72 56.2 69.8 59.6 66" />
            <path d="M20 74.4C23.2 78.4 30 80.6 40 80.4 48.8 80.2 56.4 78 59.8 74" />
            <path d="M84.4 30.6C86 46.4 85.2 62.4 82.8 78.2" />
            <path d="M82.8 78.2 76 69.4" />
            <path d="M82.8 78.2 90.8 71" />
        </Doodle>
    );
}
